import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import {
	ArrowLeft,
	Medal,
	Monitor,
	Plus,
	Sparkles,
	Trash2,
	Trophy,
} from "lucide-react";
import { useId, useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { getDb } from "@/db";
import {
	awards,
	awardWinners,
	cars,
	events,
	heatAssignments as heatAssignmentsTable,
	heats as heatsTable,
	runs,
} from "@/db/schema";
import { getSession } from "@/lib/auth-session";
import { updateLiveState } from "@/lib/live-state";
import { computeScores } from "@/lib/scoring";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Server functions ────────────────────────────────────────────────

const getAwardsData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const eventRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, event.id), eq(runs.status, "valid")));

		const eventAwards = await db
			.select()
			.from(awards)
			.where(eq(awards.eventId, event.id));

		let winners: Array<{
			id: number;
			awardId: number;
			carId: number;
			placement: number;
		}> = [];
		for (const award of eventAwards) {
			const aw = await db
				.select()
				.from(awardWinners)
				.where(eq(awardWinners.awardId, award.id));
			winners = winners.concat(aw);
		}

		return {
			event,
			cars: eventCars,
			runs: eventRuns,
			awards: eventAwards,
			winners,
		};
	});

const createAward = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			name: string;
			type: string;
			division: string | null;
		}) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();
		const existing = await db
			.select()
			.from(awards)
			.where(eq(awards.eventId, data.eventId));
		return db
			.insert(awards)
			.values({
				eventId: data.eventId,
				name: data.name,
				type: data.type,
				division: data.division,
				sortOrder: existing.length,
			})
			.returning();
	});

const deleteAward = createServerFn({ method: "POST" })
	.inputValidator((awardId: number) => awardId)
	.handler(async ({ data: awardId }) => {
		await requireAdmin();
		const db = getDb();
		await db.delete(awardWinners).where(eq(awardWinners.awardId, awardId));
		await db.delete(awards).where(eq(awards.id, awardId));
	});

const assignWinner = createServerFn({ method: "POST" })
	.inputValidator(
		(data: { awardId: number; carId: number; placement: number }) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		return getDb()
			.insert(awardWinners)
			.values({
				awardId: data.awardId,
				carId: data.carId,
				placement: data.placement,
			})
			.returning();
	});

const removeWinner = createServerFn({ method: "POST" })
	.inputValidator((winnerId: number) => winnerId)
	.handler(async ({ data: winnerId }) => {
		await requireAdmin();
		await getDb().delete(awardWinners).where(eq(awardWinners.id, winnerId));
	});

const autoAssignSpeedAwards = createServerFn({ method: "POST" })
	.inputValidator((eventId: number) => eventId)
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, eventId))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, eventId));

		const eventRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, eventId), eq(runs.status, "valid")));

		const scoringMethod = event.scoringMethod;
		const divisions: string[] = (() => {
			try {
				return JSON.parse(event.divisions);
			} catch {
				return [];
			}
		})();

		// Fetch heat data for points scoring
		const eventHeats = await db
			.select()
			.from(heatsTable)
			.where(eq(heatsTable.eventId, eventId));
		const allAssignments = [];
		for (const heat of eventHeats) {
			const ha = await db
				.select()
				.from(heatAssignmentsTable)
				.where(eq(heatAssignmentsTable.heatId, heat.id));
			allAssignments.push(...ha);
		}

		const groups: Array<{ name: string; carIds: Set<number> }> = [];
		if (divisions.length > 0) {
			for (const div of divisions) {
				groups.push({
					name: `${div} Speed Champion`,
					carIds: new Set(
						eventCars.filter((c) => c.division === div).map((c) => c.id),
					),
				});
			}
		}
		groups.push({
			name: "Overall Speed Champion",
			carIds: new Set(eventCars.map((c) => c.id)),
		});

		const allScores = computeScores(
			eventCars.map((c) => ({
				id: c.id,
				carNumber: c.carNumber,
				carName: c.carName,
				driverName: c.driverName,
				avatarUrl: c.avatarUrl,
				photoUrl: c.photoUrl,
				division: c.division,
			})),
			eventRuns.map((r) => ({
				carId: r.carId,
				elapsedTimeMs: r.elapsedTimeMs,
				lane: r.lane,
			})),
			scoringMethod,
			{
				heats: eventHeats.map((h) => ({ id: h.id, status: h.status })),
				assignments: allAssignments.map((a) => ({
					heatId: a.heatId,
					lane: a.lane,
					carId: a.carId,
				})),
				laneCount: event.laneCount,
			},
		);

		let created = 0;
		for (const group of groups) {
			const ranked = allScores.filter(
				(s) => group.carIds.has(s.car.id) && s.score != null,
			);
			if (ranked.length === 0) continue;

			const existing = await db
				.select()
				.from(awards)
				.where(and(eq(awards.eventId, eventId), eq(awards.name, group.name)))
				.get();

			let awardId: number;
			if (existing) {
				awardId = existing.id;
				await db.delete(awardWinners).where(eq(awardWinners.awardId, awardId));
			} else {
				const [newAward] = await db
					.insert(awards)
					.values({
						eventId,
						name: group.name,
						type: "speed",
						division: group.name.includes("Overall")
							? null
							: group.name.replace(" Speed Champion", ""),
						sortOrder: created,
					})
					.returning();
				awardId = newAward.id;
			}

			const top = ranked.slice(0, 3);
			for (let i = 0; i < top.length; i++) {
				await db.insert(awardWinners).values({
					awardId,
					carId: top[i].car.id,
					placement: i + 1,
				});
			}
			created++;
		}
		return { created };
	});

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/events/$eventId/awards")({
	loader: ({ params }) => getAwardsData({ data: params.eventId }),
	component: AwardsPage,
});

function AwardsPage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const queryClient = useQueryClient();
	const [confirm, confirmDialog] = useConfirm();
	const formId = useId();

	const { data } = useQuery({
		queryKey: ["awards", params.eventId],
		queryFn: () => getAwardsData({ data: params.eventId }),
		initialData,
	});

	const { event, cars: carList, awards: eventAwards, winners } = data;
	const [showForm, setShowForm] = useState(false);
	const [loading, setLoading] = useState(false);
	const [assigningAwardId, setAssigningAwardId] = useState<number | null>(null);
	const [selectedCarId, setSelectedCarId] = useState<number | "">("");

	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: ["awards", params.eventId],
		});

	async function handleCreate(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setLoading(true);
		const form = new FormData(e.currentTarget);
		await createAward({
			data: {
				eventId: event.id,
				name: form.get("name") as string,
				type: form.get("type") as string,
				division: (form.get("division") as string) || null,
			},
		});
		setLoading(false);
		setShowForm(false);
		invalidate();
	}

	async function handleDelete(awardId: number) {
		const ok = await confirm({
			title: "Delete Award",
			description: "Delete this award? This cannot be undone.",
			confirmLabel: "Delete",
			variant: "destructive",
		});
		if (!ok) return;
		await deleteAward({ data: awardId });
		invalidate();
	}

	async function handleAssign(awardId: number) {
		if (!selectedCarId) return;
		const awardWins = winners.filter((w) => w.awardId === awardId);
		await assignWinner({
			data: {
				awardId,
				carId: Number(selectedCarId),
				placement: awardWins.length + 1,
			},
		});
		setSelectedCarId("");
		setAssigningAwardId(null);
		invalidate();
	}

	async function handleRemoveWinner(winnerId: number) {
		await removeWinner({ data: winnerId });
		invalidate();
	}

	async function handleAutoAssign() {
		setLoading(true);
		await autoAssignSpeedAwards({ data: event.id });
		setLoading(false);
		invalidate();
	}

	const placementLabels = ["", "1st", "2nd", "3rd"];
	const placementColors = ["", "text-gold", "text-silver", "text-bronze"];

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			{confirmDialog}
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="flex items-center justify-between mb-6">
					<div>
						<h1 className="text-4xl text-foreground">Awards</h1>
						<p className="text-muted-foreground">{event.name}</p>
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={handleAutoAssign}
							disabled={loading}
							title="Create 1st/2nd/3rd place speed awards from race results (per division + overall)"
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
						>
							<Sparkles size={14} />
							Auto Speed Awards
						</button>
						<button
							type="button"
							onClick={() =>
								updateLiveState({
									data: { eventId: event.id, scene: { scene: "awards" } },
								})
							}
							title="Switch the TV display to show the awards ceremony"
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
						>
							<Monitor size={14} />
							Show on TV
						</button>
						<button
							type="button"
							onClick={() => setShowForm(!showForm)}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
						>
							<Plus size={14} />
							Add Award
						</button>
					</div>
				</div>

				{/* Create form */}
				{showForm && (
					<form
						onSubmit={handleCreate}
						className="bg-card rounded-xl p-4 sm:p-6 mb-6 space-y-4"
					>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div className="sm:col-span-1">
								<label
									htmlFor={`${formId}-name`}
									className="block text-sm mb-1"
								>
									Award Name
								</label>
								<input
									id={`${formId}-name`}
									name="name"
									type="text"
									required
									className="w-full px-4 py-2"
									placeholder="Best Design"
								/>
							</div>
							<div>
								<label
									htmlFor={`${formId}-type`}
									className="block text-sm mb-1"
								>
									Type
								</label>
								<select
									id={`${formId}-type`}
									name="type"
									className="w-full px-4 py-2"
								>
									<option value="custom">Custom</option>
									<option value="speed">Speed</option>
									<option value="design">Design</option>
								</select>
								<p className="text-muted-foreground text-xs mt-1">
									Category label shown on TV display
								</p>
							</div>
							{divisions.length > 0 && (
								<div>
									<label
										htmlFor={`${formId}-division`}
										className="block text-sm mb-1"
									>
										Division
									</label>
									<select
										id={`${formId}-division`}
										name="division"
										className="w-full px-4 py-2"
									>
										<option value="">All / Overall</option>
										{divisions.map((div) => (
											<option key={div} value={div}>
												{div}
											</option>
										))}
									</select>
								</div>
							)}
						</div>
						<div className="flex gap-3">
							<button
								type="submit"
								disabled={loading}
								className="px-6 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
							>
								{loading ? "Creating..." : "Create"}
							</button>
							<button
								type="button"
								onClick={() => setShowForm(false)}
								className="px-6 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
							>
								Cancel
							</button>
						</div>
					</form>
				)}

				{/* Awards list */}
				{eventAwards.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<Trophy size={48} className="text-muted-foreground mx-auto mb-4" />
						<p className="text-foreground text-lg">No awards created yet</p>
						<p className="text-muted-foreground text-sm mt-1">
							Use "Auto Speed Awards" to generate speed placements, or add
							custom awards
						</p>
					</div>
				) : (
					<div className="space-y-4">
						{eventAwards
							.sort((a, b) => a.sortOrder - b.sortOrder)
							.map((award) => {
								const awardWins = winners
									.filter((w) => w.awardId === award.id)
									.sort((a, b) => a.placement - b.placement);

								return (
									<div
										key={award.id}
										className="bg-card/50 rounded-xl border border-border p-4"
									>
										<div className="flex items-center justify-between mb-3">
											<div className="flex items-center gap-2">
												<Medal size={18} className="text-primary" />
												<span className="text-foreground font-bold text-lg">
													{award.name}
												</span>
												<span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs font-bold rounded uppercase">
													{award.type}
												</span>
												{award.division && (
													<span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
														{award.division}
													</span>
												)}
											</div>
											<button
												type="button"
												onClick={() => handleDelete(award.id)}
												className="p-1.5 text-destructive hover:text-destructive/80 hover:bg-muted rounded transition-colors"
											>
												<Trash2 size={14} />
											</button>
										</div>

										{/* Winners */}
										{awardWins.length > 0 && (
											<div className="space-y-2 mb-3">
												{awardWins.map((w) => {
													const car = carList.find((c) => c.id === w.carId);
													return (
														<div
															key={w.id}
															className="flex items-center justify-between bg-background rounded-lg px-3 py-2"
														>
															<div className="flex items-center gap-2">
																<span
																	className={`font-black text-lg ${placementColors[w.placement] || "text-muted-foreground"}`}
																>
																	{placementLabels[w.placement] ||
																		`#${w.placement}`}
																</span>
																{car?.avatarUrl && (
																	<img
																		src={car.avatarUrl}
																		alt=""
																		className="w-8 h-8 rounded object-cover"
																	/>
																)}
																<div>
																	<p className="text-foreground font-bold text-sm">
																		<span className="text-primary text-xs mr-1">
																			#{car?.carNumber}
																		</span>
																		{car?.carName}
																	</p>
																	<p className="text-muted-foreground text-xs">
																		{car?.driverName}
																	</p>
																</div>
															</div>
															<button
																type="button"
																onClick={() => handleRemoveWinner(w.id)}
																className="p-1 text-muted-foreground hover:text-destructive transition-colors"
															>
																<Trash2 size={12} />
															</button>
														</div>
													);
												})}
											</div>
										)}

										{/* Assign winner */}
										{assigningAwardId === award.id ? (
											<div className="flex items-center gap-2">
												<select
													value={selectedCarId}
													onChange={(e) =>
														setSelectedCarId(
															e.target.value ? Number(e.target.value) : "",
														)
													}
													className="flex-1 px-3 py-2 text-sm"
												>
													<option value="">Select car...</option>
													{carList
														.filter(
															(c) => !awardWins.some((w) => w.carId === c.id),
														)
														.sort((a, b) =>
															(a.carName ?? "").localeCompare(b.carName ?? ""),
														)
														.map((car) => (
															<option key={car.id} value={car.id}>
																#{car.carNumber} {car.carName ?? car.driverName}
															</option>
														))}
												</select>
												<button
													type="button"
													onClick={() => handleAssign(award.id)}
													disabled={!selectedCarId}
													className="px-3 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-sm"
												>
													Add
												</button>
												<button
													type="button"
													onClick={() => {
														setAssigningAwardId(null);
														setSelectedCarId("");
													}}
													className="px-3 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm"
												>
													Cancel
												</button>
											</div>
										) : (
											<button
												type="button"
												onClick={() => setAssigningAwardId(award.id)}
												className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
											>
												<Plus size={14} />
												Assign winner
											</button>
										)}
									</div>
								);
							})}
					</div>
				)}
			</div>
		</div>
	);
}
